import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon, Tooltip } from '@node-space/ui-components'
import { LoadingIndicator } from 'components/LoadingIndicator'

export interface EarnCustomerRewardsProgramProps {
  totalBalance: string
  epcTakesEarn: boolean
  canManageDistribution: boolean
  retainedEarningsPercentage: string
  optInRateLabel: string
  distributedEarnings: string
  optedInTotal: string
  remainingTotal: string
  onDistributeEarningsClick: () => void
  isStatisticsLoading?: boolean
}

export const EarnCustomerRewardsProgram = ({
  totalBalance,
  epcTakesEarn,
  canManageDistribution,
  retainedEarningsPercentage,
  optInRateLabel,
  distributedEarnings,
  optedInTotal,
  remainingTotal,
  onDistributeEarningsClick,
  isStatisticsLoading,
}: EarnCustomerRewardsProgramProps) => {
  const { t } = useTranslation()

  return (
    <Box
      className="rounded-[12px] overflow-hidden border border-stroke-static"
      testId="earn-customer-rewards-program"
    >
      <Box flexCol gap={16} className="bg-surface-static px-6 py-4">
        <Box flex justify="between" align="center">
          <h4 className="text-h4 text-foreground-prominent">
            {t('earn.customerRewardsProgramTitle')}
          </h4>
          {epcTakesEarn && (
            <Tooltip
              bodyContent={t('earn.customerRewardsProgramTooltip')}
              type="simple"
              position="bottom"
            >
              <Box className="cursor-default text-foreground-subdued">
                <Icon name="Info" size="medium" />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box flexCol gap={4}>
          <p className="text-label text-foreground-prominent">
            {t('earn.totalBalanceAcrossCustomers')}
          </p>
          {isStatisticsLoading ? (
            <LoadingIndicator
              text={t('earn.calculatingEllipses')}
              testId="earn-total-balance-skeleton"
            />
          ) : (
            <h3 className="text-h3 text-foreground-prominent">{totalBalance}</h3>
          )}
        </Box>

        {epcTakesEarn ? (
          <Box flexCol gap={16}>
            <Box flex gap={16}>
              <Box flexCol gap={4} className="flex-1 min-w-0">
                <p className="text-body-strong-small text-foreground-subdued">
                  {t('earn.optInRate')}
                </p>
                {isStatisticsLoading ? (
                  <LoadingIndicator
                    text={t('earn.calculatingEllipses')}
                    testId="earn-opt-in-rate-skeleton"
                  />
                ) : (
                  <h3 className="text-h3 text-foreground-prominent">{optInRateLabel}</h3>
                )}
              </Box>
              <Box flexCol gap={4} className="flex-1 min-w-0">
                <p className="text-body-strong-small text-foreground-subdued">
                  {t('earn.distributedEarningsToCustomersLabel')}
                </p>
                <h3 className="text-h3 text-foreground-prominent">{distributedEarnings}</h3>
              </Box>
            </Box>
            <Box flex gap={16}>
              <Box flexCol gap={4} className="flex-1 min-w-0">
                <p className="text-body-strong-small text-foreground-subdued">
                  {t('earn.optedInTotal')}
                </p>
                <h3 className="text-h3 text-foreground-prominent">{optedInTotal}</h3>
              </Box>
              <Box flexCol gap={4} className="flex-1 min-w-0">
                <p className="text-body-strong-small text-foreground-subdued">
                  {t('earn.remainingTotal')}
                </p>
                <h3 className="text-h3 text-foreground-prominent">{remainingTotal}</h3>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box flexCol gap={4}>
            <p className="text-body-strong-small text-foreground-subdued">
              {t('earn.retainedCustomerEarningsPercentage')}
            </p>
            <h3 className="text-h3 text-foreground-prominent">{retainedEarningsPercentage}</h3>
          </Box>
        )}
      </Box>

      {!epcTakesEarn &&
        (canManageDistribution ? (
          <Box
            flex
            gap={4}
            align="center"
            className="w-full border-t border-stroke-static bg-surface-interactive px-6 py-4"
          >
            <p className="text-label flex-1 text-left text-foreground-prominent">
              {t('earn.retainedEarningsDescription', { percentage: retainedEarningsPercentage })}{' '}
              <Button
                noPadding
                variant="ghost"
                className="inline text-label text-foreground-action-secondary"
                onClick={onDistributeEarningsClick}
              >
                {t('earn.enableCustomerRewardsDistribution')}
              </Button>
            </p>
            <Box
              className="cursor-pointer text-foreground-action-secondary"
              onClick={onDistributeEarningsClick}
            >
              <Icon name="ArrowRight" size="medium" />
            </Box>
          </Box>
        ) : (
          <Box
            flex
            gap={4}
            align="center"
            className="w-full border-t border-stroke-static bg-surface-interactive px-6 py-4"
            testId="earn-customer-rewards-program-no-permission"
          >
            <p className="text-label flex-1 text-left text-foreground-prominent">
              {t('earn.retainedEarningsDescription', { percentage: retainedEarningsPercentage })}{' '}
              {t('earn.workspaceOwnerCanEnableDistribution')}
            </p>
            <Icon name="ArrowRight" size="medium" />
          </Box>
        ))}
    </Box>
  )
}
